import { useState, useEffect } from 'react';
import { Pin, PinOff, ChevronRight } from 'lucide-react';
import { api } from '../api';
import {
  Card, Button, Pill, Table, THead, TH, TR, TD, Input,
  fmtR, fmtDate, fmtDT, Empty,
} from './ui';

/**
 * Per-project rate-card panel.
 *
 * Hybrid behaviour:
 *   - No pin → auto-suggested snapshot from the rate card active on
 *     the project's start_date (today if start_date is null).
 *   - Pinned → snapshot of the rate card chosen at pin time. Frozen
 *     even when newer rate cards are published.
 *
 * Re-pinning is audited (every pin row supersedes the previous via
 * superseded_at / superseded_by_pin_id; nothing is ever deleted).
 */
export default function ProjectRateCardPanel({ projectId }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = () => {
    if (!projectId) return;
    setMsg(null);
    api.projects
      .rateCard(projectId)
      .then(setData)
      .catch((e) => setMsg({ kind: 'err', text: e.message }));
  };
  useEffect(load, [projectId]);

  if (!projectId) return null;
  if (!data) {
    return (
      <Card title="Rate card applied" subtitle="Loading…">
        <div className="h-6 w-48 bg-slate-700 rounded animate-pulse" />
      </Card>
    );
  }

  const { auto, pinned, applied, history } = data;
  const isPinned = !!pinned;
  const divergent = isPinned && auto.snapshot_from !== pinned.snapshot_from;

  const pinNow = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.projects.pinRateCard(projectId, {
        pinned_rate_card_date: auto.snapshot_from,
        notes: notes || null,
      });
      setNotes('');
      load();
      setMsg({ kind: 'ok', text: `Pinned to version effective ${fmtDate(auto.snapshot_from)}.` });
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const unpin = async () => {
    if (!window.confirm('Unpin this project? It will fall back to auto-suggest.')) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.projects.unpinRateCard(projectId);
      load();
      setMsg({ kind: 'ok', text: 'Pin removed.' });
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Rate card applied to this project"
      subtitle={
        isPinned
          ? `Pinned by ${pinned.pinned_by} on ${fmtDT(pinned.pinned_at)} · version ${fmtDate(pinned.snapshot_from)}`
          : `Auto · using rate card active on ${fmtDate(auto.basis_date)} (${auto.basis === 'project_start_date' ? 'project start' : 'today'}) · version ${fmtDate(auto.snapshot_from)}`
      }
      action={
        <div className="flex items-center gap-3">
          <Pill tone={isPinned ? 'accent' : 'neutral'}>
            {isPinned ? 'Pinned' : 'Auto'}
          </Pill>
          {isPinned ? (
            <Button variant="secondary" size="sm" onClick={unpin} loading={busy}>
              <PinOff className="w-3.5 h-3.5" strokeWidth={1.75} /> Unpin
            </Button>
          ) : (
            <Button size="sm" onClick={pinNow} loading={busy}>
              <Pin className="w-3.5 h-3.5" strokeWidth={1.75} /> Pin this version
            </Button>
          )}
        </div>
      }
      padding="p-0"
    >
      {!isPinned && (
        <div className="px-6 pt-4">
          <Input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional pin reason (e.g. locked at quote for FY26)"
            className="w-full"
          />
        </div>
      )}

      {divergent && (
        <div className="mx-6 mt-4 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/20 text-xs text-amber-700 dark:text-amber-400">
          A newer rate card (effective {fmtDate(auto.snapshot_from)}) has been published since this project was pinned.
          The pinned version is still being applied to calculations.
        </div>
      )}

      <Table className="mt-4">
        <THead>
          <TH>Role</TH>
          <TH align="right">Sale rate</TH>
          <TH>Effective from</TH>
          <TH>Captured by</TH>
        </THead>
        <tbody>
          {applied.rates.length === 0 ? (
            <tr>
              <td colSpan={4}>
                <Empty>No rate card rows for this snapshot.</Empty>
              </td>
            </tr>
          ) : (
            applied.rates.map((r) => (
              <TR key={`${r.role}-${r.effective_from}`}>
                <TD>{r.role}</TD>
                <TD align="right" mono>{fmtR(r.sale_rate_per_hour)} / h</TD>
                <TD muted mono>{fmtDate(r.effective_from)}</TD>
                <TD muted>{r.created_by || '—'}</TD>
              </TR>
            ))
          )}
        </tbody>
      </Table>

      {msg && (
        <p className={`px-6 py-3 text-xs ${msg.kind === 'ok' ? 'text-emerald-600' : 'text-brand-500'}`}>
          {msg.text}
        </p>
      )}

      <button
        onClick={() => setShowHistory((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-3 text-xs text-slate-500 hover:text-slate-200 border-t border-slate-700 transition-colors"
      >
        <span>Pin history · {history.length} {history.length === 1 ? 'entry' : 'entries'}</span>
        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showHistory ? 'rotate-90' : ''}`} strokeWidth={1.75} />
      </button>

      {showHistory && (
        <Table>
          <THead>
            <TH>Rate card from</TH>
            <TH>Pinned by</TH>
            <TH>Pinned at</TH>
            <TH>Superseded at</TH>
            <TH>Reason</TH>
          </THead>
          <tbody>
            {history.length === 0 ? (
              <tr><td colSpan={5}><Empty>No pin history yet.</Empty></td></tr>
            ) : history.map((h) => (
              <TR key={h.pin_id}>
                <TD mono>{fmtDate(h.pinned_rate_card_date)}</TD>
                <TD muted>{h.pinned_by}</TD>
                <TD muted mono>{fmtDT(h.pinned_at)}</TD>
                <TD muted mono>{fmtDT(h.superseded_at)}</TD>
                <TD muted className="max-w-xs truncate">{h.notes || ''}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
